import React from 'react';
import { ExternalLink } from 'lucide-react';

export function labelize(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase()) || '-';
}

export function displayValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (typeof value === 'object') {
    if (Object.prototype.hasOwnProperty.call(value, 'value')) return displayValue(value.value);
    return JSON.stringify(value);
  }
  return String(value);
}

function badgeClass(kind = 'neutral') {
  if (kind === 'success') return 'border-green-200 bg-green-50 text-green-700';
  if (kind === 'warning') return 'border-yellow-200 bg-yellow-50 text-yellow-700';
  if (kind === 'danger') return 'border-red-200 bg-red-50 text-red-700';
  if (kind === 'accent') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (kind === 'purple') return 'border-purple-200 bg-purple-50 text-purple-700';
  return 'border-stone-200 bg-stone-50 text-stone-600';
}

export function ProposalBadge({ kind = 'neutral', children }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${badgeClass(kind)}`}>
      {children}
    </span>
  );
}

function buttonClass(kind = 'secondary') {
  if (kind === 'primary') return 'border-stone-900 bg-stone-900 text-white hover:bg-stone-800';
  if (kind === 'accent') return 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100';
  if (kind === 'danger') return 'border-red-200 bg-white text-red-700 hover:bg-red-50';
  return 'border-stone-300 bg-white text-stone-700 hover:bg-stone-50';
}

export function ProposalButton({ kind, loading, children, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`rounded-lg border px-3 py-2 text-xs font-medium disabled:opacity-50 ${buttonClass(kind)}`}
    >
      {loading ? 'Applying...' : children}
    </button>
  );
}

function normalizeEditableValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function parseEditedValue(raw, originalValue) {
  const text = String(raw || '').trim();
  if (Array.isArray(originalValue) || (originalValue && typeof originalValue === 'object')) {
    if (!text) return Array.isArray(originalValue) ? [] : {};
    return JSON.parse(text);
  }
  return text;
}

export function statusKind(status) {
  if (status === 'approved' || status === 'added_to_agent_note' || status === 'tag_proposed' || status === 'kept') return 'success';
  if (status === 'rejected' || status === 'dismissed') return 'warning';
  if (status === 'pending' || status === 'proposed' || status === 'needs_followup') return 'accent';
  return 'neutral';
}

export function compactList(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map(item => String(item).trim()).filter(Boolean);
  if (value === null || value === undefined || value === '') return [];
  return [String(value).trim()].filter(Boolean);
}

function hasProvenance(provenance) {
  return Boolean(
    provenance &&
    typeof provenance === 'object' &&
    Object.keys(provenance).some(key => {
      const value = provenance[key];
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    })
  );
}

function provenanceBasisKind(basis) {
  if (basis === 'stated') return 'success';
  if (basis === 'inferred') return 'warning';
  if (basis === 'mixed') return 'accent';
  return 'neutral';
}

function formatProvenanceConfidence(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return `${Math.round(value * 100)}%`;
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 0 && numeric <= 1) return `${Math.round(numeric * 100)}%`;
  return labelize(value);
}

function signalTitle(signal) {
  const value = signal.value ? labelize(signal.value) : '';
  const signalName = signal.signal ? labelize(signal.signal) : '';
  const family = signal.signal_family ? labelize(signal.signal_family) : '';
  if (value && signalName && value.toLowerCase() !== signalName.toLowerCase()) return `${value} (${signalName})`;
  return value || signalName || family || signal.display_text || 'Signal proposal';
}

function signalContext(signal) {
  return [signal.signal_family, signal.signal].filter(Boolean).map(labelize).join(' / ');
}

export function ProposalProvenance({ provenance }) {
  if (!hasProvenance(provenance)) return null;
  const attributeRefs = compactList(provenance.attribute_refs);
  const evidenceRefs = compactList(provenance.evidence_refs);
  const confidence = formatProvenanceConfidence(provenance.confidence);
  const versions = [
    provenance.extraction_model_version ? `Extraction: ${provenance.extraction_model_version}` : null,
    provenance.generation_model_version ? `Generation: ${provenance.generation_model_version}` : null,
  ].filter(Boolean);

  return (
    <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50/40 p-2 text-xs text-stone-600">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-blue-900">Provenance</span>
        {provenance.basis_summary && (
          <ProposalBadge kind={provenanceBasisKind(provenance.basis_summary)}>
            {labelize(provenance.basis_summary)}
          </ProposalBadge>
        )}
        {confidence && <ProposalBadge>Confidence {confidence}</ProposalBadge>}
        {provenance.generation_step && <ProposalBadge>{labelize(provenance.generation_step)}</ProposalBadge>}
      </div>
      {(attributeRefs.length > 0 || evidenceRefs.length > 0 || versions.length > 0) && (
        <div className="mt-2 space-y-1">
          {attributeRefs.length > 0 && <p><span className="text-stone-400">Attributes:</span> {attributeRefs.join(', ')}</p>}
          {evidenceRefs.length > 0 && <p><span className="text-stone-400">Evidence refs:</span> {evidenceRefs.join(', ')}</p>}
          {versions.length > 0 && <p><span className="text-stone-400">Models:</span> {versions.join(' / ')}</p>}
        </div>
      )}
    </div>
  );
}

export function editableProposalValue(value) {
  return normalizeEditableValue(value);
}

export function parseEditedProposalValue(raw, originalValue) {
  return parseEditedValue(raw, originalValue);
}

export function FieldProposalItem({
  field,
  requestId,
  editing = false,
  draftValue = '',
  savingAction = null,
  onDraftChange,
  onStartEdit,
  onCancelEdit,
  onReviewStatus,
  onApplyEdited,
}) {
  const isPending = ['pending', 'needs_followup'].includes(field.field_status);
  const target = field.target_label || field.target_field || field.target_table || 'field';
  const status = field.field_status || 'pending';

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-3 text-sm">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <p className="font-medium text-stone-800">{labelize(target)}</p>
        {field.target_table && <ProposalBadge>{field.target_table}</ProposalBadge>}
        <ProposalBadge kind={statusKind(status)}>{labelize(status)}</ProposalBadge>
        {field.confidence && <span className="text-xs text-stone-500">Confidence: {labelize(field.confidence)}</span>}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <p className="text-xs font-medium text-stone-400">Current</p>
          <p className="whitespace-pre-wrap break-words text-stone-500 line-through">{displayValue(field.old_value)}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-stone-400">Proposed</p>
          <p className="whitespace-pre-wrap break-words text-stone-900">{displayValue(field.proposed_value)}</p>
        </div>
      </div>

      {(field.evidence_snippet || field.change_reason || field.source_url) && (
        <div className="mt-3 rounded-lg bg-stone-50 p-2 text-xs text-stone-600">
          {field.change_reason && <p>{field.change_reason}</p>}
          {field.evidence_snippet && <p className="mt-1">{field.evidence_snippet}</p>}
          {field.source_url && (
            <a href={field.source_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-blue-700">
              Field evidence <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      <ProposalProvenance provenance={field.provenance} />

      {isPending && (
        <div className="mt-3 flex flex-wrap gap-2">
          <ProposalButton kind="primary" onClick={() => onReviewStatus?.('approved')} disabled={Boolean(savingAction)} loading={savingAction === 'approved'}>
            Approve + apply
          </ProposalButton>
          <ProposalButton kind="accent" onClick={onStartEdit} disabled={Boolean(savingAction)}>
            Edit proposal
          </ProposalButton>
          <ProposalButton onClick={() => onReviewStatus?.('needs_followup')} disabled={Boolean(savingAction)} loading={savingAction === 'needs_followup'}>
            Needs follow-up
          </ProposalButton>
          <ProposalButton kind="danger" onClick={() => onReviewStatus?.('rejected')} disabled={Boolean(savingAction)} loading={savingAction === 'rejected'}>
            Reject
          </ProposalButton>
        </div>
      )}

      {isPending && editing && (
        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/40 p-3">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-blue-800">Edited proposal</span>
            <textarea
              rows={Array.isArray(field.proposed_value) || (field.proposed_value && typeof field.proposed_value === 'object') ? 5 : 3}
              value={draftValue}
              onChange={event => onDraftChange?.(event.target.value)}
              className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm leading-6 text-stone-900 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
            />
          </label>
          {(Array.isArray(field.proposed_value) || (field.proposed_value && typeof field.proposed_value === 'object')) && (
            <p className="mt-1 text-xs text-blue-800">Structured values must stay valid JSON.</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <ProposalButton kind="accent" onClick={onApplyEdited} disabled={Boolean(savingAction)} loading={savingAction === 'approved'}>
              Apply edited
            </ProposalButton>
            <ProposalButton onClick={onCancelEdit} disabled={Boolean(savingAction)}>
              Cancel
            </ProposalButton>
          </div>
        </div>
      )}
    </div>
  );
}

export function SignalProposalItem({
  signal,
  savingVerdict = null,
  onVerdict,
}) {
  const status = signal.status || 'proposed';
  const assignments = Array.isArray(signal.assignments) ? signal.assignments : [];
  const isMatchAssignment = signal.item_type === 'match_signal_assignment' || assignments.length > 0;
  const title = isMatchAssignment
    ? `${assignments.length || 1} match signal assignment${(assignments.length || 1) === 1 ? '' : 's'}`
    : signalTitle(signal);
  const context = isMatchAssignment ? 'Governed card routing signals' : signalContext(signal);
  const isCardScoped = signal.scope === 'card' || Boolean(signal.card_key);
  const evidenceLinks = signal.evidence_links || [];
  const duplicateCount = signal.duplicate_count || evidenceLinks.length || 0;
  const tier = signal.blocking || signal.tier === 'blocking'
    ? 'blocking'
    : (signal.tier === 'correction' ? 'correction' : 'suggestion');
  const tierBadge = tier === 'blocking'
    ? ['danger', 'Blocks publishing']
    : (tier === 'correction' ? ['accent', 'Correction'] : ['accent', 'Suggestion']);

  return (
    <div className={`rounded-xl border bg-white p-4 ${tier === 'blocking' ? 'border-red-200' : 'border-blue-200'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <ProposalBadge kind={tierBadge[0]}>{tierBadge[1]}</ProposalBadge>
        <ProposalBadge kind={isCardScoped ? 'accent' : 'purple'}>{isCardScoped ? `Card: ${labelize(signal.card_key)}` : 'Place signal'}</ProposalBadge>
        {isMatchAssignment && <ProposalBadge kind="accent">Match signals</ProposalBadge>}
        {signal.source_type && <ProposalBadge kind="purple">{labelize(signal.source_type)}</ProposalBadge>}
        {!isMatchAssignment && duplicateCount > 1 && <ProposalBadge>{duplicateCount} merged signals</ProposalBadge>}
        <p className="text-sm font-medium text-stone-900">{title}</p>
        <ProposalBadge kind={statusKind(status)}>{labelize(status)}</ProposalBadge>
        {signal.confidence && <span className="text-xs text-stone-500">Confidence: {labelize(signal.confidence)}</span>}
      </div>
      <p className="mt-2 text-xs text-stone-500">
        {isMatchAssignment
          ? 'Approved assignments write the governed match-signal table. Composer only sees them after publish projection.'
          : (tier === 'blocking'
          ? 'Blocking signals must be reviewed before publishing.'
          : (tier === 'correction' ? 'Corrections propose a field-level or factual change.' : 'Suggestions are optional and never block publishing.'))}
      </p>
      {context && <p className="mt-1 text-xs text-stone-500">Signal: {context}</p>}
      {isMatchAssignment && assignments.length > 0 && (
        <div className="mt-3 grid gap-2">
          {assignments.map((assignment, index) => (
            <div key={assignment.id || `${assignment.dimension}-${assignment.value}-${index}`} className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 text-sm text-stone-700">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <ProposalBadge kind="accent">{labelize(assignment.dimension)}</ProposalBadge>
                  <span className="font-medium text-stone-900">{assignment.label || labelize(assignment.value)}</span>
                  {assignment.basis && <ProposalBadge>{labelize(assignment.basis)}</ProposalBadge>}
                  {assignment.confidence && <span className="text-xs text-stone-500">Confidence: {labelize(assignment.confidence)}</span>}
                </div>
                {status === 'proposed' && assignment.id && (
                  <div className="flex flex-wrap gap-2">
                    {[
                      ['approve_match_signals', 'Approve', 'accent'],
                      ['dismiss', 'Dismiss', 'danger'],
                    ].map(([verdict, label, kind]) => {
                      const savingKey = `${verdict}:${assignment.id}`;
                      return (
                        <ProposalButton
                          key={verdict}
                          kind={kind}
                          onClick={() => onVerdict?.(verdict, {
                            signalId: assignment.id,
                            signalIds: [assignment.id],
                            savingKey,
                          })}
                          disabled={Boolean(savingVerdict)}
                          loading={savingVerdict === savingKey}
                        >
                          {label}
                        </ProposalButton>
                      );
                    })}
                  </div>
                )}
              </div>
              {assignment.evidence_snippet && <p className="mt-2 text-xs text-stone-600">{assignment.evidence_snippet}</p>}
              {assignment.source_url && (
                <a href={assignment.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-blue-700">
                  Evidence source <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}
      {signal.source_url && (
        <a href={signal.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-blue-700">
          Evidence source <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {(signal.evidence_snippet || signal.value) && (
        <div className="mt-3 rounded-lg bg-stone-50 p-3 text-sm text-stone-700">
          {signal.value && <p><span className="text-stone-400">Proposed value:</span> {labelize(signal.value)}</p>}
          {signal.evidence_snippet && <p className="mt-1">{signal.evidence_snippet}</p>}
        </div>
      )}
      {evidenceLinks.length > 1 && (
        <details className="mt-3 rounded-lg border border-stone-200 bg-stone-50/60">
          <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-stone-600">
            Evidence from {evidenceLinks.length} signal rows
          </summary>
          <div className="grid gap-2 border-t border-stone-200 p-3 text-xs text-stone-600">
            {evidenceLinks.map((evidence, index) => (
              <div key={evidence.id || index} className="rounded-md bg-white p-2">
                {evidence.evidence_snippet && <p>{evidence.evidence_snippet}</p>}
                {evidence.source_url && (
                  <a href={evidence.source_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-blue-700">
                    Source {evidence.id ? `#${evidence.id}` : index + 1} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {evidence.confidence && <p className="mt-1 text-stone-400">Confidence: {labelize(evidence.confidence)}</p>}
              </div>
            ))}
          </div>
        </details>
      )}
      {status === 'proposed' && !(isMatchAssignment && assignments.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {(isMatchAssignment ? [
            ['approve_match_signals', 'Approve assignments', 'accent'],
            ['dismiss', 'Dismiss', 'danger'],
          ] : [
            ['add_to_agent_note', 'Add to agent note', 'accent'],
            ['propose_tag', 'Propose tag', 'secondary'],
            ['keep', 'Keep', 'secondary'],
            ['dismiss', 'Dismiss', 'danger'],
          ]).map(([verdict, label, kind]) => (
            <ProposalButton
              key={verdict}
              kind={kind}
              onClick={() => onVerdict?.(verdict)}
              disabled={Boolean(savingVerdict)}
              loading={savingVerdict === verdict}
            >
              {label}
            </ProposalButton>
          ))}
        </div>
      )}
    </div>
  );
}

export function WarningProposalItem({
  warning,
  savingVerdict = null,
  onVerdict,
}) {
  const title = warning.target_label || warning.warning_code || 'Warning';
  const scope = warning.scope || 'card';
  const isComputed = warning.clearing === 'computed';

  return (
    <div className="rounded-xl border border-red-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <ProposalBadge kind={isComputed ? 'warning' : 'danger'}>{isComputed ? 'Needs fix' : 'Blocks publishing'}</ProposalBadge>
        <ProposalBadge kind={scope === 'place' ? 'purple' : 'accent'}>{labelize(scope)}</ProposalBadge>
        <p className="text-sm font-medium text-stone-900">{title}</p>
        {warning.responsible_party && <ProposalBadge>{labelize(warning.responsible_party)}</ProposalBadge>}
        {warning.demotes_published && <ProposalBadge kind="danger">Demotes live card</ProposalBadge>}
      </div>
      <div className="mt-3 rounded-lg bg-red-50/70 p-3 text-sm text-stone-700">
        <p>{warning.warning_code}</p>
        {warning.blast_radius && <p className="mt-1 text-xs text-red-700">{warning.blast_radius}</p>}
      </div>
      {isComputed ? (
        <p className="mt-3 text-xs text-stone-500">
          This is a computed card gap. Fix the field on the card and the item clears automatically; there is no separate resolve or dismiss verdict.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <ProposalButton kind="primary" onClick={() => onVerdict?.('resolve')} disabled={Boolean(savingVerdict)} loading={savingVerdict === 'resolve'}>
            Resolve verified/fixed
          </ProposalButton>
          <ProposalButton kind="danger" onClick={() => onVerdict?.('dismiss')} disabled={Boolean(savingVerdict)} loading={savingVerdict === 'dismiss'}>
            Dismiss not applicable
          </ProposalButton>
        </div>
      )}
    </div>
  );
}
